import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { toast } from 'sonner';
import {
  HelpCircle,
  BookOpen,
  Video,
  MessageCircle,
  Mail,
  Phone,
  ExternalLink,
  Lightbulb,
  Sparkles,
  Users,
  Tractor,
  Calculator,
  Store,
  Crown,
  Settings,
  FileText,
  PlayCircle,
  CheckCircle2,
  Facebook,
  Instagram,
  Copy,
  Download,
  Share2,
  Key,
  Shield,
  AlertTriangle,
} from 'lucide-react';

export default function AidePage() {
  const [copied, setCopied] = useState(false);

  const getClaudeInstructionsText = () => {
    return `=== INSTRUCTIONS POUR CLAUDE ===
Bonjour Claude, j'ai besoin de ton aide pour connecter ma page Facebook a mon logiciel Bureau Agricole.

Mon logiciel a besoin d'un "Token d'acces" Facebook avec les bonnes permissions pour pouvoir publier automatiquement du contenu sur ma page Facebook professionnelle.

Voici ce que tu dois m'aider a faire, etape par etape :

ETAPE 1 : Aller sur Meta for Developers
- Ouvre un navigateur et va sur : https://developers.facebook.com/
- Connecte-toi avec le compte Facebook qui gere ta page professionnelle
- Si tu n'as pas de compte developpeur, il va te demander d'en creer un (c'est gratuit, clique juste sur "Commencer")

ETAPE 2 : Creer une application (si pas deja fait)
- Dans le menu en haut, clique sur "Mes applications"
- Clique sur "Creer une application"
- Type : choisis "Entreprise" (Business)
- Nom : mets ce que tu veux, par exemple "Bureau Agricole Publishing"
- Email : ton email
- Valide la creation

ETAPE 3 : Aller dans l'API Explorer
- Va sur : https://developers.facebook.com/tools/explorer/
- En haut a droite, selectionne ton application dans le menu deroulant
- C'est ICI que tu vas generer le token

ETAPE 4 : Cocher les permissions (TRES IMPORTANT)
- Clique sur "Add a Permission" ou "Ajouter une permission"
- Tu dois cocher TOUTES ces cases :
  * pages_manage_posts (pour publier sur ta page)
  * pages_read_engagement (pour lire les statistiques)
  * pages_show_list (pour voir la liste de tes pages)
  * instagram_basic (si tu veux aussi Instagram)
  * instagram_content_publish (pour publier sur Instagram)
  * public_profile (normalement deja coche)

ETAPE 5 : Generer le token
- Clique sur le bouton bleu "Generate Access Token" (Generer un token d'acces)
- Une fenetre Facebook va s'ouvrir
- Connecte-toi et autorise tout
- Tu vas recevoir un LONG texte (le token) dans le champ en haut

ETAPE 6 : Transformer en token longue duree (IMPORTANT)
- Le token genere ne dure que 1 heure
- Pour le rendre permanent, va sur :
  https://developers.facebook.com/tools/debug/accesstoken/
- Colle ton token
- Clique sur "Etendre le token d'acces" en bas de la page
- Copie le NOUVEAU token (celui de longue duree)

ETAPE 7 : Donner le token a Bureau Agricole
- Retourne dans Bureau Agricole
- Ouvre le chat avec l'assistant
- Tape : "connecte facebook [COLLE LE TOKEN ICI]"
- L'assistant va confirmer que la connexion est faite

PROBLEMES FREQUENTS :
- "Erreur de permission" : tu n'as pas coche pages_manage_posts a l'etape 4
- "Token expire" : tu n'as pas fait l'etape 6 (token longue duree)
- "Aucune page trouvee" : ton compte Facebook doit etre administrateur d'une Page Facebook (pas un simple profil)

Si tu as des questions, demande a Claude de t'expliquer chaque etape en detail.
=== FIN DES INSTRUCTIONS ===`;
  };
  const guides = [
    {
      category: 'Démarrage',
      icon: Sparkles,
      items: [
        {
          title: 'Premiers pas avec Bureau Agricole',
          description: 'Configurez votre exploitation et commencez à utiliser l\'application',
          duration: '5 min',
        },
        {
          title: 'Ajouter votre équipe',
          description: 'Invitez vos collaborateurs et définissez leurs accès',
          duration: '3 min',
        },
        {
          title: 'Configurer vos parcelles',
          description: 'Importez ou dessinez vos parcelles sur la carte',
          duration: '10 min',
        },
      ]
    },
    {
      category: 'Équipe & RH',
      icon: Users,
      items: [
        {
          title: 'Gérer les fiches collaborateurs',
          description: 'Documents, certifications, heures et congés',
          duration: '5 min',
        },
        {
          title: 'Suivi des absences',
          description: 'Déclarer et suivre les absences (maladie, retard...)',
          duration: '3 min',
        },
        {
          title: 'Évaluations et compétences',
          description: 'Évaluer vos collaborateurs et suivre leur progression',
          duration: '5 min',
        },
      ]
    },
    {
      category: 'Exploitation',
      icon: Tractor,
      items: [
        {
          title: 'Enregistrer une intervention',
          description: 'Saisir vos travaux sur les parcelles',
          duration: '3 min',
        },
        {
          title: 'Gérer vos stocks',
          description: 'Phytosanitaires, semences, engrais...',
          duration: '5 min',
        },
        {
          title: 'Registre phytosanitaire',
          description: 'Générer votre registre conforme PAC',
          duration: '5 min',
        },
      ]
    },
    {
      category: 'Comptabilité',
      icon: Calculator,
      items: [
        {
          title: 'Connexion bancaire',
          description: 'Synchronisez vos comptes bancaires',
          duration: '5 min',
        },
        {
          title: 'Factures et devis',
          description: 'Créer et envoyer vos documents commerciaux',
          duration: '5 min',
        },
      ]
    },
    {
      category: 'Marketing & IA',
      icon: Store,
      items: [
        {
          title: 'Créer votre site web',
          description: 'Générez un site vitrine en quelques clics',
          duration: '10 min',
        },
        {
          title: 'Studio IA',
          description: 'Créez du contenu marketing avec l\'IA',
          duration: '5 min',
        },
      ]
    },
    {
      category: 'Assistante IA',
      icon: Crown,
      items: [
        {
          title: 'Utiliser l\'assistant',
          description: 'Posez vos questions et obtenez de l\'aide',
          duration: '3 min',
        },
        {
          title: 'Enregistrer une réunion',
          description: 'Transcription automatique de vos appels',
          duration: '5 min',
        },
        {
          title: 'Actions automatiques',
          description: 'Laissez l\'IA exécuter des tâches pour vous',
          duration: '5 min',
        },
      ]
    },
    {
      category: 'Assistant Visio',
      icon: Video,
      items: [
        {
          title: 'Connecter vos visios (Gmail)',
          description: 'Detectez automatiquement vos invitations Zoom, Meet, Teams',
          duration: '2 min',
          detailed: true,
        },
        {
          title: 'Analyser un enregistrement',
          description: 'Uploadez une video et obtenez transcription + resume + actions',
          duration: '5 min',
          detailed: true,
        },
        {
          title: 'Recuperer votre enregistrement Zoom',
          description: 'Ou trouver le fichier video de votre reunion Zoom',
          duration: '3 min',
        },
        {
          title: 'Recuperer votre enregistrement Google Meet',
          description: 'Ou trouver le fichier video de votre reunion Meet',
          duration: '3 min',
        },
      ]
    },
    {
      category: 'Intégrations (Gmail, Drive, Sociaux...)',
      icon: Settings,
      items: [
        {
          title: 'Connecter votre compte Google',
          description: 'Gmail, Drive et Agenda en 2 minutes',
          duration: '2 min',
          detailed: true,
        },
        {
          title: 'Facebook & Instagram',
          description: 'Publiez automatiquement sur vos reseaux sociaux via l\'IA',
          duration: '10 min',
          detailed: true,
        },
        {
          title: 'Deleguer via le chat',
          description: 'Confiez des taches a vos collaborateurs depuis l\'assistant',
          duration: '2 min',
        },
      ]
    },
  ];

  const faq = [
    {
      question: 'Comment inviter un collaborateur ?',
      answer: 'Allez dans Équipe & RH > Collaborateurs, cliquez sur "Ajouter un collaborateur" et envoyez-lui le code d\'invitation par email ou SMS.'
    },
    {
      question: 'Comment générer mon registre phytosanitaire ?',
      answer: 'Dans Exploitation > Registre Phyto, sélectionnez la période souhaitée et cliquez sur "Exporter PDF". Le registre sera généré automatiquement à partir de vos interventions.'
    },
    {
      question: 'L\'enregistrement de réunion ne fonctionne pas',
      answer: 'Assurez-vous d\'avoir autorisé l\'accès au microphone dans votre navigateur. Si le problème persiste, essayez avec Chrome ou Firefox.'
    },
    {
      question: 'Comment synchroniser mon compte bancaire ?',
      answer: 'Dans Comptabilité, cliquez sur "Connexion bancaire" et suivez les étapes de sécurité. La synchronisation est sécurisée via notre partenaire agréé ACPR.'
    },
    {
      question: 'Puis-je utiliser l\'application sur mobile ?',
      answer: 'Oui ! Bureau Agricole est entièrement responsive. Vous pouvez l\'utiliser depuis n\'importe quel appareil avec un navigateur web.'
    },
    {
      question: 'Comment annuler mon abonnement ?',
      answer: 'Dans Paramètres > Tarifs & Facturation, cliquez sur "Gérer mon abonnement". Vous pouvez annuler à tout moment, l\'accès reste actif jusqu\'à la fin de la période payée.'
    },
    {
      question: '📧 Comment connecter mon Gmail à Bureau Agricole ?',
      answer: `C'est très simple ! Voici les étapes :

1️⃣ Allez dans le menu "Paramètres" (en bas à gauche)
2️⃣ Cliquez sur "Google" dans le sous-menu
3️⃣ Cliquez sur le bouton vert "Connecter avec Google"
4️⃣ Une fenêtre Google s'ouvre : connectez-vous avec votre adresse Gmail
5️⃣ Cliquez sur "Autoriser" quand Google demande les permissions
6️⃣ C'est terminé ! Vous verrez "Compte connecté" avec une coche verte ✅

⚠️ Si vous avez un message d'erreur, vérifiez que vous utilisez bien Chrome ou Firefox (pas Safari).`
    },
    {
      question: '🔒 Est-ce que c\'est sécurisé de connecter mon Gmail ?',
      answer: `Oui, c'est totalement sécurisé ! Bureau Agricole utilise la connexion officielle de Google (OAuth 2.0).

✅ Nous ne voyons JAMAIS votre mot de passe Google
✅ Vous pouvez déconnecter à tout moment
✅ Google vous montre exactement quels accès vous donnez
✅ Vos emails restent sur les serveurs de Google

C'est le même système utilisé par les grandes applications comme Outlook, Slack, etc.`
    },
    {
      question: '❌ J\'ai une erreur "Application non autorisée" ou "invalid_client"',
      answer: `Cette erreur peut arriver. Voici comment la résoudre :

1️⃣ Fermez complètement votre navigateur
2️⃣ Rouvrez-le et retournez sur bureauagricole.fr
3️⃣ Reconnectez-vous à Bureau Agricole
4️⃣ Réessayez de connecter Google

Si ça ne marche toujours pas, contactez le support : contact@bureauagricole.fr`
    },
    {
      question: '📱 Je peux connecter plusieurs adresses Gmail ?',
      answer: `Non, vous ne pouvez connecter qu'UN seul compte Google par exploitation.

Mais si vous avez plusieurs exploitations dans Bureau Agricole, chaque exploitation peut avoir son propre compte Google connecté.

💡 Conseil : Utilisez l'adresse email principale de votre exploitation (celle que vos clients connaissent).`
    },
    {
      question: '📹 Comment fonctionne l\'Assistant Visio ?',
      answer: `L'Assistant Visio vous aide à gérer vos visioconférences :

1️⃣ DÉTECTER VOS RÉUNIONS :
• Connectez votre Gmail (Paramètres > Google)
• L'assistant scanne vos emails
• Il trouve automatiquement les invitations Zoom, Meet, Teams
• Vous voyez la liste de vos réunions à venir

2️⃣ ANALYSER UN ENREGISTREMENT :
• Après une visio, téléchargez l'enregistrement
• Uploadez-le dans Assistant Visio > Analyser
• L'IA transcrit l'audio en texte
• Vous obtenez un résumé + les actions à faire !`
    },
    {
      question: '📥 Comment récupérer mon enregistrement Zoom ?',
      answer: `Pour récupérer votre enregistrement Zoom :

1️⃣ Connectez-vous sur zoom.us
2️⃣ Allez dans "Réunions" > "Enregistrements"
3️⃣ Trouvez votre réunion dans la liste
4️⃣ Cliquez sur "Télécharger" (fichier MP4)
5️⃣ Le fichier se télécharge sur votre ordinateur
6️⃣ Uploadez-le dans Bureau Agricole > Assistant Visio

💡 Astuce : Si vous avez enregistré en local, le fichier est dans Documents/Zoom/`
    },
    {
      question: '📥 Comment récupérer mon enregistrement Google Meet ?',
      answer: `Pour récupérer votre enregistrement Google Meet :

1️⃣ Allez sur drive.google.com
2️⃣ Cherchez le dossier "Meet Recordings"
3️⃣ Trouvez votre réunion (triée par date)
4️⃣ Clic droit > "Télécharger"
5️⃣ Le fichier se télécharge sur votre ordinateur
6️⃣ Uploadez-le dans Bureau Agricole > Assistant Visio

⚠️ Note : L'enregistrement Meet nécessite Google Workspace (payant).`
    },
    {
      question: '📥 Comment récupérer mon enregistrement Teams ?',
      answer: `Pour récupérer votre enregistrement Microsoft Teams :

1️⃣ Ouvrez l'application Teams
2️⃣ Allez dans "Chat" ou "Réunions"
3️⃣ Trouvez la réunion enregistrée
4️⃣ Cliquez sur "..." puis "Ouvrir dans OneDrive"
5️⃣ Téléchargez le fichier depuis OneDrive
6️⃣ Uploadez-le dans Bureau Agricole > Assistant Visio

💡 L'enregistrement peut mettre quelques minutes à apparaître après la réunion.`
    },
    {
      question: 'Combien de temps prend l\'analyse d\'une visio ?',
      answer: `Le temps dépend de la durée de votre enregistrement :

• 15 minutes de visio → ~2 minutes d'analyse
• 30 minutes de visio → ~4 minutes d'analyse  
• 1 heure de visio → ~8 minutes d'analyse

Vous pouvez fermer la page, l'analyse continue en arrière-plan !
Vous retrouverez le résultat dans l'onglet "Analyses".`
    },
    {
      question: 'Comment connecter Facebook a Bureau Agricole ?',
      answer: `Pour publier automatiquement sur votre page Facebook, il faut un "Token d'acces" :

1. Allez sur developers.facebook.com (connectez-vous avec le compte qui gere votre Page)
2. Allez dans Outils > API Explorer (ou directement : developers.facebook.com/tools/explorer/)
3. Cochez les permissions : pages_manage_posts, pages_read_engagement, pages_show_list
4. Cliquez "Generate Access Token"
5. Autorisez votre compte dans la fenetre qui s'ouvre
6. Copiez le token genere
7. Dans Bureau Agricole, ouvrez le chat et tapez : "connecte facebook [votre token]"

Astuce : Telechargez le guide pour Claude depuis la section Facebook ci-dessus, il vous accompagnera pas a pas !`
    },
    {
      question: 'Mon token Facebook ne fonctionne pas, pourquoi ?',
      answer: `Les 3 causes les plus frequentes :

1. PERMISSIONS MANQUANTES : Vous n'avez pas coche "pages_manage_posts" dans l'API Explorer. Refaites la procedure en cochant bien cette case.

2. TOKEN EXPIRE : Le token de base ne dure qu'1 heure. Allez sur developers.facebook.com/tools/debug/accesstoken/ et cliquez "Etendre le token" pour le rendre permanent.

3. PAS ADMINISTRATEUR : Vous devez etre administrateur de la Page Facebook (pas un simple profil personnel). Verifiez dans les parametres de votre Page > Roles de la Page.`
    },
    {
      question: 'Comment publier sur Instagram via Bureau Agricole ?',
      answer: `Instagram fonctionne via Facebook (Meta) :

1. Votre compte Instagram doit etre un compte "Professionnel" (pas Personnel)
2. Il doit etre lie a votre Page Facebook
3. Lors de la generation du token, cochez aussi : instagram_basic et instagram_content_publish
4. Le reste est identique a la procedure Facebook

Ensuite, demandez a l'assistant : "publie sur instagram [votre texte]" et il s'en charge !`
    },
  ];

  return (
    <div className="p-4 md:p-6 space-y-6 max-w-5xl mx-auto">
      {/* Header */}
      <div className="text-center">
        <h1 className="text-3xl font-bold flex items-center justify-center gap-3 mb-2">
          <HelpCircle className="w-8 h-8 text-primary" />
          Aide & Guide
        </h1>
        <p className="text-muted-foreground">
          Tout ce dont vous avez besoin pour maîtriser Bureau Agricole
        </p>
      </div>

      {/* Guide rapide connexion Google - Mis en avant */}
      <Card className="bg-gradient-to-r from-blue-50 to-green-50 dark:from-blue-950/30 dark:to-green-950/30 border-blue-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Mail className="w-5 h-5 text-blue-600" />
            📧 Connecter votre Gmail en 2 minutes
          </CardTitle>
          <CardDescription>
            Synchronisez vos emails, Drive et agenda avec Bureau Agricole
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* Étapes visuelles */}
            <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
              <div className="flex flex-col items-center text-center p-3 bg-white dark:bg-gray-900 rounded-lg">
                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center text-xl font-bold text-blue-600 mb-2">1</div>
                <p className="text-sm font-medium">Menu Paramètres</p>
                <p className="text-xs text-muted-foreground">En bas à gauche</p>
              </div>
              <div className="flex flex-col items-center text-center p-3 bg-white dark:bg-gray-900 rounded-lg">
                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center text-xl font-bold text-blue-600 mb-2">2</div>
                <p className="text-sm font-medium">Cliquez "Google"</p>
                <p className="text-xs text-muted-foreground">Dans le sous-menu</p>
              </div>
              <div className="flex flex-col items-center text-center p-3 bg-white dark:bg-gray-900 rounded-lg">
                <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center text-xl font-bold text-green-600 mb-2">3</div>
                <p className="text-sm font-medium">Bouton vert</p>
                <p className="text-xs text-muted-foreground">"Connecter Google"</p>
              </div>
              <div className="flex flex-col items-center text-center p-3 bg-white dark:bg-gray-900 rounded-lg">
                <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center text-xl font-bold text-orange-600 mb-2">4</div>
                <p className="text-sm font-medium">Connexion Google</p>
                <p className="text-xs text-muted-foreground">Votre email + mot de passe</p>
              </div>
              <div className="flex flex-col items-center text-center p-3 bg-white dark:bg-gray-900 rounded-lg">
                <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center text-xl font-bold text-green-600 mb-2">✓</div>
                <p className="text-sm font-medium">Terminé !</p>
                <p className="text-xs text-muted-foreground">Coche verte = connecté</p>
              </div>
            </div>
            
            {/* Conseils */}
            <div className="flex items-start gap-3 p-3 bg-yellow-50 dark:bg-yellow-950/30 rounded-lg border border-yellow-200">
              <Lightbulb className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="font-medium text-yellow-800 dark:text-yellow-200">Conseils :</p>
                <ul className="text-yellow-700 dark:text-yellow-300 mt-1 space-y-1">
                  <li>• Utilisez <strong>Chrome</strong> ou <strong>Firefox</strong> (pas Safari)</li>
                  <li>• Connectez l'email principal de votre exploitation</li>
                  <li>• Vous pouvez déconnecter à tout moment</li>
                </ul>
              </div>
            </div>
            
            <Button 
              className="w-full md:w-auto"
              onClick={() => window.location.href = '/parametres/google'}
            >
              <Mail className="w-4 h-4 mr-2" />
              Aller aux paramètres Google
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Guide rapide Assistant Visio */}
      <Card className="bg-gradient-to-r from-purple-50 to-blue-50 dark:from-purple-950/30 dark:to-blue-950/30 border-purple-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Video className="w-5 h-5 text-purple-600" />
            📹 Assistant Visio - Analysez vos réunions
          </CardTitle>
          <CardDescription>
            Transcription automatique et résumé de vos visioconférences
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* Étapes visuelles */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <div className="flex flex-col items-center text-center p-3 bg-white dark:bg-gray-900 rounded-lg">
                <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center text-xl font-bold text-purple-600 mb-2">1</div>
                <p className="text-sm font-medium">Faites votre visio</p>
                <p className="text-xs text-muted-foreground">Zoom, Meet ou Teams</p>
              </div>
              <div className="flex flex-col items-center text-center p-3 bg-white dark:bg-gray-900 rounded-lg">
                <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center text-xl font-bold text-purple-600 mb-2">2</div>
                <p className="text-sm font-medium">Téléchargez l'enregistrement</p>
                <p className="text-xs text-muted-foreground">Fichier MP4 ou MP3</p>
              </div>
              <div className="flex flex-col items-center text-center p-3 bg-white dark:bg-gray-900 rounded-lg">
                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center text-xl font-bold text-blue-600 mb-2">3</div>
                <p className="text-sm font-medium">Uploadez dans Bureau Agricole</p>
                <p className="text-xs text-muted-foreground">Assistant Visio {">"} Analyser</p>
              </div>
              <div className="flex flex-col items-center text-center p-3 bg-white dark:bg-gray-900 rounded-lg">
                <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center text-xl font-bold text-green-600 mb-2">✓</div>
                <p className="text-sm font-medium">Résultat !</p>
                <p className="text-xs text-muted-foreground">Transcription + Résumé + Actions</p>
              </div>
            </div>
            
            {/* Où trouver l'enregistrement */}
            <div className="flex items-start gap-3 p-3 bg-blue-50 dark:bg-blue-950/30 rounded-lg border border-blue-200">
              <Lightbulb className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="font-medium text-blue-800 dark:text-blue-200">Où trouver votre enregistrement ?</p>
                <ul className="text-blue-700 dark:text-blue-300 mt-1 space-y-1">
                  <li>• <strong>Zoom</strong> : zoom.us {">"} Réunions {">"} Enregistrements {">"} Télécharger</li>
                  <li>• <strong>Google Meet</strong> : drive.google.com {">"} Meet Recordings</li>
                  <li>• <strong>Teams</strong> : Chat de la réunion {">"} ... {">"} Ouvrir dans OneDrive</li>
                </ul>
              </div>
            </div>
            
            <Button 
              className="w-full md:w-auto"
              onClick={() => window.location.href = '/assistant-visio'}
            >
              <Video className="w-4 h-4 mr-2" />
              Ouvrir l'Assistant Visio
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Guide Facebook & Instagram */}
      <Card className="bg-gradient-to-r from-blue-50 to-pink-50 dark:from-blue-950/30 dark:to-pink-950/30 border-blue-300" data-testid="facebook-guide-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Share2 className="w-5 h-5 text-blue-600" />
            Facebook & Instagram - Publier avec l'IA
          </CardTitle>
          <CardDescription>
            Laissez l'assistant generer et publier du contenu saisonnier sur vos reseaux sociaux
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
              <div className="flex flex-col items-center text-center p-3 bg-white dark:bg-gray-900 rounded-lg">
                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center text-xl font-bold text-blue-600 mb-2">1</div>
                <p className="text-sm font-medium">Allez sur Meta</p>
                <p className="text-xs text-muted-foreground">developers.facebook.com</p>
              </div>
              <div className="flex flex-col items-center text-center p-3 bg-white dark:bg-gray-900 rounded-lg">
                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center text-xl font-bold text-blue-600 mb-2">2</div>
                <p className="text-sm font-medium">API Explorer</p>
                <p className="text-xs text-muted-foreground">Outils {">"} API Explorer</p>
              </div>
              <div className="flex flex-col items-center text-center p-3 bg-white dark:bg-gray-900 rounded-lg">
                <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center text-xl font-bold text-orange-600 mb-2">3</div>
                <p className="text-sm font-medium">Cochez les droits</p>
                <p className="text-xs text-muted-foreground">pages_manage_posts + pages_read_engagement</p>
              </div>
              <div className="flex flex-col items-center text-center p-3 bg-white dark:bg-gray-900 rounded-lg">
                <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center text-xl font-bold text-orange-600 mb-2">4</div>
                <p className="text-sm font-medium">Generer le token</p>
                <p className="text-xs text-muted-foreground">Cliquez "Generate Access Token"</p>
              </div>
              <div className="flex flex-col items-center text-center p-3 bg-white dark:bg-gray-900 rounded-lg">
                <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center text-xl font-bold text-green-600 mb-2">5</div>
                <p className="text-sm font-medium">Copiez le token</p>
                <p className="text-xs text-muted-foreground">Le long texte genere</p>
              </div>
              <div className="flex flex-col items-center text-center p-3 bg-white dark:bg-gray-900 rounded-lg">
                <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center text-xl font-bold text-green-600 mb-2">6</div>
                <p className="text-sm font-medium">Collez dans le chat</p>
                <p className="text-xs text-muted-foreground">"connecte facebook [token]"</p>
              </div>
            </div>

            <div className="flex items-start gap-3 p-3 bg-red-50 dark:bg-red-950/30 rounded-lg border border-red-200">
              <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="font-medium text-red-800 dark:text-red-200">Important : Les permissions</p>
                <p className="text-red-700 dark:text-red-300 mt-1">
                  Sans cocher <strong>pages_manage_posts</strong> et <strong>pages_read_engagement</strong>, le token ne pourra PAS publier sur votre page. C'est la cause d'erreur la plus frequente.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3 p-3 bg-purple-50 dark:bg-purple-950/30 rounded-lg border border-purple-200">
              <Lightbulb className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="font-medium text-purple-800 dark:text-purple-200">Astuce : Utilisez Claude pour vous aider</p>
                <p className="text-purple-700 dark:text-purple-300 mt-1">
                  Telechargez le texte ci-dessous et collez-le dans Claude. Il vous guidera etape par etape.
                </p>
              </div>
            </div>

            <div className="flex gap-3">
              <Button 
                data-testid="copy-claude-text-btn"
                variant="outline"
                onClick={() => {
                  const claudeText = getClaudeInstructionsText();
                  navigator.clipboard.writeText(claudeText).then(() => {
                    toast.success('Texte copie ! Collez-le dans Claude.');
                  }).catch(() => {
                    toast.error('Erreur de copie. Telechargez le fichier a la place.');
                  });
                }}
              >
                <Copy className="w-4 h-4 mr-2" />
                Copier le texte pour Claude
              </Button>
              <Button
                data-testid="download-claude-text-btn"
                variant="outline"
                onClick={() => {
                  const claudeText = getClaudeInstructionsText();
                  const blob = new Blob([claudeText], { type: 'text/plain;charset=utf-8' });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = 'instructions_facebook_pour_claude.txt';
                  a.click();
                  URL.revokeObjectURL(url);
                  toast.success('Fichier telecharge !');
                }}
              >
                <Download className="w-4 h-4 mr-2" />
                Telecharger le fichier .txt
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Actions rapides */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="hover:bg-muted/50 cursor-pointer transition-colors">
          <CardContent className="p-4 flex flex-col items-center text-center">
            <div className="w-12 h-12 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center mb-3">
              <Video className="w-6 h-6 text-blue-600" />
            </div>
            <p className="font-medium">Tutoriels vidéo</p>
            <p className="text-xs text-muted-foreground">Guides pas à pas</p>
          </CardContent>
        </Card>
        
        <Card className="hover:bg-muted/50 cursor-pointer transition-colors">
          <CardContent className="p-4 flex flex-col items-center text-center">
            <div className="w-12 h-12 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center mb-3">
              <MessageCircle className="w-6 h-6 text-green-600" />
            </div>
            <p className="font-medium">Chat support</p>
            <p className="text-xs text-muted-foreground">Réponse immédiate</p>
          </CardContent>
        </Card>
        
        <Card className="hover:bg-muted/50 cursor-pointer transition-colors">
          <CardContent className="p-4 flex flex-col items-center text-center">
            <div className="w-12 h-12 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center mb-3">
              <Mail className="w-6 h-6 text-purple-600" />
            </div>
            <p className="font-medium">Email</p>
            <p className="text-xs text-muted-foreground">support@bureau-agricole.fr</p>
          </CardContent>
        </Card>
        
        <Card className="hover:bg-muted/50 cursor-pointer transition-colors">
          <CardContent className="p-4 flex flex-col items-center text-center">
            <div className="w-12 h-12 rounded-full bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center mb-3">
              <Phone className="w-6 h-6 text-orange-600" />
            </div>
            <p className="font-medium">Téléphone</p>
            <p className="text-xs text-muted-foreground">04 XX XX XX XX</p>
          </CardContent>
        </Card>
      </div>

      {/* Guides par catégorie */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BookOpen className="w-5 h-5" />
            Guides par module
          </CardTitle>
          <CardDescription>
            Apprenez à utiliser chaque fonctionnalité
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-6 md:grid-cols-2">
            {guides.map((category) => (
              <div key={category.category} className="space-y-3">
                <h3 className="font-semibold flex items-center gap-2">
                  <category.icon className="w-5 h-5 text-primary" />
                  {category.category}
                </h3>
                <div className="space-y-2">
                  {category.items.map((item, i) => (
                    <div
                      key={i}
                      className="p-3 rounded-lg border hover:bg-muted/50 cursor-pointer transition-colors"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <p className="font-medium text-sm">{item.title}</p>
                          <p className="text-xs text-muted-foreground">{item.description}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant="secondary" className="text-xs">
                            {item.duration}
                          </Badge>
                          <PlayCircle className="w-4 h-4 text-muted-foreground" />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* FAQ */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Lightbulb className="w-5 h-5 text-amber-500" />
            Questions fréquentes
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Accordion type="single" collapsible className="w-full">
            {faq.map((item, i) => (
              <AccordionItem key={i} value={`item-${i}`}>
                <AccordionTrigger className="text-left">
                  {item.question}
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground">
                  {item.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </CardContent>
      </Card>

      {/* Nouveautés */}
      <Card className="border-primary/20 bg-primary/5">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-primary" />
            Nouveautés récentes
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {[
              { title: 'Import Geofolia via le chat (parcelles, materiel, collaborateurs)', date: 'Mars 2026', new: true },
              { title: 'Delegation de taches aux collaborateurs via l\'assistant', date: 'Mars 2026', new: true },
              { title: 'Briefing IA matinal sur le Dashboard', date: 'Mars 2026', new: true },
              { title: 'Connexion reseaux sociaux (Facebook, Instagram)', date: 'Mars 2026', new: true },
              { title: 'Transcription de reunions en temps reel', date: 'Fevrier 2026' },
              { title: 'Module Absences & Alertes RH', date: 'Fevrier 2026' },
            ].map((feature, i) => (
              <div key={i} className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-green-500" />
                  <span className="text-sm">{feature.title}</span>
                  {feature.new && <Badge className="text-xs">Nouveau</Badge>}
                </div>
                <span className="text-xs text-muted-foreground">{feature.date}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
